'use client';

import Link from 'next/link';
import React, { useEffect, useRef, useState } from 'react';

import OpenStore from '@/components/widgets/OpenStore';

import HeaderLogo from './HeaderLogo';
import { menu } from './menus';

export default function Header() {
  const [scroll, setScroll] = useState<boolean>(false);
  const [open, setOpen] = useState(false);
  const containerRef = useRef<HTMLHeadElement>(null);

  useEffect(() => {
    const clickHandler = ({ target }: MouseEvent) => {
      if (
        containerRef.current &&
        containerRef.current.contains(target as Node)
      ) {
        return;
      }
      setOpen(false);
    };

    const scrollHandler = () => {
      if (window.scrollY > 0) {
        setScroll(true);
      } else {
        setScroll(false);
      }
    };

    const keyHandler = ({ keyCode }: KeyboardEvent) => {
      if (keyCode !== 27) return;
      setOpen(false);
    };

    scrollHandler();
    document.addEventListener('mousedown', clickHandler);
    document.addEventListener('keydown', keyHandler);
    document.addEventListener('scroll', scrollHandler);

    return () => {
      document.removeEventListener('mousedown', clickHandler);
      document.removeEventListener('keydown', keyHandler);
      document.removeEventListener('scroll', scrollHandler);
    };
  }, []);

  return (
    <>
      <div
        className={`fixed top-0 h-screen w-full ${
          open ? 'z-40 bg-black/40' : '-z-50'
        }`}
      />
      <header
        ref={containerRef}
        className={`fixed inset-x-0 top-0 z-50 m-0 ${
          scroll || open
            ? 'bg-white shadow-lg shadow-black/5'
            : 'bg-transparent shadow-none'
        }`}
      >
        <nav className="section section-container flex h-16 flex-row justify-between py-0 sm:h-20 lg:h-24">
          <div className="flex gap-5">
            <HeaderLogo />
            <div className="my-auto hidden grow gap-4 xl:flex">
              {menu.map((item, i) => (
                <div key={i} className="font-medium">
                  <Link
                    id={`header.${item.title}`}
                    href={item.url || '/'}
                    className="relative rounded-full px-6 py-2 text-primary-950 hover:bg-primary-100 hover:text-primary-600"
                    onClick={(e) => {
                      if (!item.url) e.preventDefault();
                      if (item.action) {
                        item.action();
                      }
                    }}
                  >
                    {item.title}
                  </Link>
                  {item.subMenus && (
                    <div className="relative hidden flex-col group-hover:block">
                      <ul className="absolute rounded bg-white px-5 py-2 shadow-md">
                        {item.subMenus.map((subMenu, subMenuIndex) => (
                          <li key={subMenuIndex}>
                            <Link
                              href={subMenu.url || '/'}
                              onClick={(e) => {
                                if (!subMenu.url) e.preventDefault();
                                if (subMenu.action) {
                                  subMenu.action();
                                }
                              }}
                            >
                              <div className="py-1">
                                <span className="whitespace-nowrap py-5 text-primary-950 hover:text-primary-600">
                                  {/* {t(`header.${subMenu.title}`)} */}
                                </span>
                              </div>
                            </Link>
                          </li>
                        ))}
                      </ul>
                    </div>
                  )}
                </div>
              ))}
            </div>
          </div>
          <div className="my-auto flex flex-1 flex-row justify-end space-x-4">
            {/* <div className="group my-auto hidden flex-col items-end justify-center hover:cursor-pointer md:flex">
                <div className="relative px-5 py-2">
                  <span
                    className={`inline-block transition-opacity duration-300 ease-out will-change-opacity hover:opacity-50 lg:text-base ${
                      scroll || open ? 'text-primary-950' : 'text-white'
                    }`}
                  >
                    <span className="mr-2">{getLanguageFlag(locale)}</span>
                    {t(`languages.${locale}`)}
                  </span>
                </div>
                <div className="relative hidden w-max flex-col items-end group-hover:flex">
                  <ul className="absolute rounded bg-white px-5 py-2 text-right shadow-md">
                    {locales
                      ?.filter((lang) => lang !== locale)
                      .map((lang) => (
                        <li key={lang} className="m-0">
                          <LanguageSwitcher locale={lang}>
                            <span className="inline-block w-max text-justify text-primary-950 transition-opacity duration-300 ease-out will-change-opacity hover:opacity-50 lg:text-base">
                              <span className="mr-2">
                                {getLanguageFlag(lang)}
                              </span>
                              {t(`languages.${lang}`)}
                            </span>
                          </LanguageSwitcher>
                        </li>
                      ))}
                  </ul>
                </div>
              </div> */}
            <div className="fixed bottom-5 left-0 flex w-full justify-center px-8 sm:static sm:bottom-auto sm:left-auto sm:flex sm:w-auto sm:px-0">
              <OpenStore />
            </div>
            <div
              className="group my-auto p-2 duration-300 ease-in hover:cursor-pointer lg:hidden"
              onClick={() => setOpen((old) => !old)}
            >
              <div className="pointer-events-none relative flex size-4 flex-col justify-between">
                <span
                  className={`mb-1 block h-0.5 w-4 origin-top-left bg-primary-400 transition-hamburger duration-300 ease-out will-change-hamburger ${
                    open ? 'rotate-45 scale-x-[1.27]' : ''
                  }`}
                />
                <span
                  className={`mb-1 block h-0.5 w-4 bg-primary-400 transition-hamburger duration-300 ease-out will-change-hamburger ${
                    open ? 'opacity-0' : ''
                  }`}
                />
                <span
                  className={`block h-0.5 w-4 origin-bottom-left bg-primary-400 transition-hamburger duration-300 ease-out will-change-hamburger ${
                    open ? '-rotate-45 scale-x-[1.27]' : ''
                  }`}
                />
              </div>
            </div>
          </div>
        </nav>
        <div
          className={`overflow-hidden transition-all duration-100 ease-in will-change-opacity-transform ${
            open ? 'max-h-screen shadow' : 'max-h-0'
          }`}
          onClick={() => setOpen(false)}
        >
          <div className="relative mx-auto px-5 py-3 sm:container md:max-w-screen-xl">
            <div>
              <ul className="flex flex-col">
                {menu.map((item, i) => (
                  <li key={i} className="items-center px-5 py-2">
                    <Link
                      href={item.url || '/'}
                      className="text-primary-950 hover:opacity-50"
                      onClick={(e) => {
                        if (!item.url) e.preventDefault();
                        if (item.action) {
                          item.action();
                        }
                      }}
                    >
                      {item.title}
                    </Link>
                    <ul>
                      {item.subMenus?.map((subMenu, subMenuIndex) => (
                        <li
                          key={subMenuIndex}
                          className="items-center px-5 py-2"
                        >
                          <Link
                            href={subMenu.url || '/'}
                            className="text-primary-950 hover:opacity-50"
                            onClick={(e) => {
                              if (!subMenu.url) e.preventDefault();
                              if (subMenu.action) {
                                subMenu.action();
                              }
                            }}
                          >
                            {item.title}
                          </Link>
                        </li>
                      ))}
                    </ul>
                  </li>
                ))}
              </ul>
              {/* <ul className="flex flex-col">
                {locales.map((lang) => (
                  <li key={lang} className="px-5 py-2">
                    <LanguageSwitcher locale={lang}>
                      <span className="inline-block w-max text-justify text-primary-950 transition-opacity duration-300 ease-out will-change-opacity hover:opacity-50 xl:text-base">
                        <span className="mr-2">{getLanguageFlag(lang)}</span>
                        {t(`languages.${lang}`)}
                      </span>
                    </LanguageSwitcher>
                  </li>
                ))}
              </ul> */}
            </div>
          </div>
        </div>
      </header>
    </>
  );
}
